import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api, money, mediaUrl, apiErr } from "@/lib/api";
import { Table, Input, Select, Button, StatusPill, Badge, Field, SortControl, sortRows } from "@/lib/ui";

const UNITS = ["Box", "Piece", "Sq.ft", "Kg", "Set", "Pair", "Meter", "Bundle"];

function Uploader({ label, value, accept, onChange }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const isVideo = accept.includes("video");
  const upload = async (file) => {
    if (!file) return; setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url); toast.success(`${label} updated`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div>
      <p className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
      <div onClick={() => ref.current.click()} className="flex aspect-video cursor-pointer items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 hover:border-orange-400">
        {busy ? <i className="fa-solid fa-spinner fa-spin text-slate-400" /> :
          value ? (isVideo ? <video src={mediaUrl(value)} className="h-full w-full object-cover" /> : <img src={mediaUrl(value)} className="h-full w-full object-cover" alt={label} />) :
            <span className="text-xs text-slate-400"><i className={`fa-solid ${isVideo ? "fa-video" : "fa-camera"} mr-1`} />Upload</span>}
      </div>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={(e) => upload(e.target.files[0])} />
    </div>
  );
}

export default function AdminProducts() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ categories: [], sizes: [], cities: [], localities: [] });
  const [q, setQ] = useState({ search: "", category: "", status: "" });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sort, setSort] = useState({ field: "product", dir: "asc" });
  const SORT_FIELDS = [
    { key: "product", label: "Product Name", type: "str" },
    { key: "category", label: "Category", type: "str" },
    { key: "company_name", label: "Seller", type: "str" },
    { key: "primary_rate", label: "Rate", type: "num" },
    { key: "discount", label: "Discount", type: "num" },
    { key: "views", label: "Views", type: "num" },
  ];

  useEffect(() => { api.get("/public/filters").then(({ data }) => setFilters(data)); }, []);
  const load = useCallback(() => {
    const params = {};
    Object.entries(q).forEach(([k, v]) => v && (params[k] = v));
    api.get("/admin/products", { params }).then(({ data }) => setRows(data));
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const set = (k, v) => setQ((p) => ({ ...p, [k]: v }));
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v, ...(k === "city" ? { locality: "" } : {}) }));

  const openEdit = (p) => {
    setForm({
      category: p.category || "", product: p.product || "", size: p.size || "", quality: p.quality || "", description: p.description || "", keywords: p.keywords || "",
      condition: p.condition || "New", quantity: p.quantity ?? "", moq: p.moq ?? "",
      primary_unit: p.primary_unit || "Box", secondary_unit: p.secondary_unit || "",
      actual_rate: p.actual_rate ?? "", primary_rate: p.primary_rate ?? "", secondary_rate: p.secondary_rate ?? "",
      catalogue_image: p.catalogue_image || "", stock_image: p.stock_image || "", stock_video: p.stock_video || "",
      city: p.city || "", locality: p.locality || "",
      manufacturer_stock: !!p.manufacturer_stock, pan_india: !!p.pan_india, allow_buyers: !!p.allow_buyers,
      approval: p.approval || "pending", status: p.status || "inactive",
    });
    setEditing(p);
  };

  const save = async () => {
    if (!form.category || !form.product || !form.size || !form.city || !form.locality) { toast.error("Fill all required fields"); return; }
    if (!form.catalogue_image || !form.stock_image) { toast.error("Both images are required"); return; }
    setBusy(true);
    try {
      await api.put(`/admin/products/${editing.id}`, {
        ...form, quantity: Number(form.quantity) || 0, moq: Number(form.moq) || 0,
        actual_rate: Number(form.actual_rate), primary_rate: Number(form.primary_rate),
        secondary_rate: Number(form.secondary_rate) || 0,
      });
      toast.success("Product updated"); setEditing(null); setForm(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.product}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/products/${p.id}`); toast.success("Product deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const approve = async (p) => {
    try { await api.post(`/admin/products/${p.id}/approve`); toast.success(`"${p.product}" approved & live`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const reject = async (p) => {
    const reason = window.prompt(`Reject "${p.product}"? Optional reason for the seller:`, "");
    if (reason === null) return;
    try { await api.post(`/admin/products/${p.id}/reject`, { reason }); toast.success(`"${p.product}" rejected`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const discount = form && form.actual_rate && form.primary_rate && Number(form.actual_rate) > 0
    ? Math.max(0, Math.round(((Number(form.actual_rate) - Number(form.primary_rate)) / Number(form.actual_rate)) * 100)) : 0;
  const locs = form ? filters.localities.filter((l) => !form.city || l.city === form.city) : [];

  return (
    <div>
      <h1 className="font-head text-2xl font-extrabold text-slate-900">All Products</h1>
      <div className="mt-4 mb-4 flex flex-wrap gap-2">
        <Input className="max-w-xs" placeholder="Search product / company…" value={q.search} onChange={(e) => set("search", e.target.value)} data-testid="ap-search" />
        <Select className="w-auto" value={q.category} onChange={(e) => set("category", e.target.value)} data-testid="ap-cat"><option value="">All Categories</option>{filters.categories.map((c) => <option key={c.id}>{c.name}</option>)}</Select>
        <Select className="w-auto" value={q.status} onChange={(e) => set("status", e.target.value)} data-testid="ap-status"><option value="">All Status</option><option value="approved">Approved</option><option value="pending">Pending</option><option value="rejected">Rejected</option></Select>
        <Button variant="outline" onClick={() => setQ({ search: "", category: "", status: "" })}><i className="fa-solid fa-rotate-left" /> Reset</Button>
        <div className="ml-auto"><SortControl fields={SORT_FIELDS} value={sort} onChange={setSort} testid="ap-sort" /></div>
      </div>

      <Table head={["Product", "Category", "Seller", "City", "Rate", "Discount", "Flags", "Views", "Status", "Actions"]}>
        {sortRows(rows, SORT_FIELDS, sort).map((p) => (
          <tr key={p.id} data-testid={`admin-product-${p.id}`}>
            <td className="px-4 py-3 font-semibold text-slate-800">{p.product}<div className="text-xs text-slate-400">{p.size}</div></td>
            <td className="px-4 py-3">{p.category}</td>
            <td className="px-4 py-3">{p.company_name}<div className="text-xs capitalize text-slate-400">{p.seller_type}</div></td>
            <td className="px-4 py-3">{p.city}</td>
            <td className="px-4 py-3 font-semibold">{money(p.primary_rate)}</td>
            <td className="px-4 py-3">{p.discount}%</td>
            <td className="px-4 py-3">{p.manufacturer_stock && <Badge tone="blue">Mfg</Badge>} {p.pan_india && <Badge tone="orange">PI</Badge>}</td>
            <td className="px-4 py-3">{p.views}</td>
            <td className="px-4 py-3"><StatusPill status={p.approval === "approved" ? p.status : p.approval} /></td>
            <td className="px-4 py-3">
              <div className="flex gap-2">
                {p.approval !== "approved" && (
                  <button onClick={() => approve(p)} data-testid={`admin-product-approve-${p.id}`} title="Approve & publish" className="flex h-9 w-9 items-center justify-center rounded-lg border border-emerald-200 text-emerald-600 hover:bg-emerald-50"><i className="fa-solid fa-check" /></button>
                )}
                {p.approval !== "rejected" && (
                  <button onClick={() => reject(p)} data-testid={`admin-product-reject-${p.id}`} title="Reject" className="flex h-9 w-9 items-center justify-center rounded-lg border border-amber-200 text-amber-600 hover:bg-amber-50"><i className="fa-solid fa-ban" /></button>
                )}
                <button onClick={() => openEdit(p)} data-testid={`admin-product-edit-${p.id}`} title="Edit" className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"><i className="fa-solid fa-pen" /></button>
                <button onClick={() => del(p)} data-testid={`admin-product-del-${p.id}`} title="Delete" className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-red-500 hover:bg-red-50"><i className="fa-solid fa-trash" /></button>
              </div>
            </td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-400">No products</td></tr>}
      </Table>

      {editing && form && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" onClick={() => setEditing(null)}>
          <div className="my-6 w-full max-w-2xl rounded-2xl bg-white p-6 ds-scroll" onClick={(e) => e.stopPropagation()} data-testid="admin-product-edit-modal">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-head text-xl font-extrabold text-slate-900">Edit Product</h2>
                <p className="text-xs text-slate-400">{editing.company_name} · {editing.seller_type}</p>
              </div>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700" data-testid="admin-product-edit-close"><i className="fa-solid fa-xmark text-lg" /></button>
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <Field label="Category" required><Select value={form.category} onChange={(e) => setF("category", e.target.value)} data-testid="ape-category"><option value="">Select</option>{filters.categories.map((c) => <option key={c.id}>{c.name}</option>)}</Select></Field>
              <Field label="Product Name" required><Input value={form.product} onChange={(e) => setF("product", e.target.value)} data-testid="ape-product" /></Field>
              <Field label="Size" required><Select value={form.size} onChange={(e) => setF("size", e.target.value)}><option value="">Select</option>{filters.sizes.map((s) => <option key={s.id}>{s.name}</option>)}</Select></Field>
              <Field label="Quality"><Input value={form.quality} onChange={(e) => setF("quality", e.target.value)} /></Field>
              <div className="sm:col-span-2">
                <Field label="Product Description">
                  <textarea data-testid="ape-description" value={form.description} onChange={(e) => setF("description", e.target.value)} rows={4}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" placeholder="Add a detailed description of the product…" />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Search Keywords">
                  <Input value={form.keywords} onChange={(e) => setF("keywords", e.target.value)} data-testid="ape-keywords" placeholder="Comma-separated: sofa, couch, wooden furniture" />
                </Field>
              </div>
              <Field label="Condition"><Select value={form.condition} onChange={(e) => setF("condition", e.target.value)}><option>New</option><option>Used</option><option>Refurbished</option></Select></Field>
              <Field label="Quantity" required><Input type="number" value={form.quantity} onChange={(e) => setF("quantity", e.target.value)} /></Field>
              <Field label="MOQ"><Input type="number" value={form.moq} onChange={(e) => setF("moq", e.target.value)} /></Field>
              <Field label="Primary Unit"><Select value={form.primary_unit} onChange={(e) => setF("primary_unit", e.target.value)}>{UNITS.map((u) => <option key={u}>{u}</option>)}</Select></Field>
              <Field label="Secondary Unit"><Select value={form.secondary_unit} onChange={(e) => setF("secondary_unit", e.target.value)}><option value="">None</option>{UNITS.map((u) => <option key={u}>{u}</option>)}</Select></Field>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <Field label="Actual Rate (₹)" required><Input type="number" value={form.actual_rate} onChange={(e) => setF("actual_rate", e.target.value)} /></Field>
              <Field label="Primary Rate (₹)" required><Input type="number" value={form.primary_rate} onChange={(e) => setF("primary_rate", e.target.value)} /></Field>
              <Field label="Secondary Rate (₹)"><Input type="number" value={form.secondary_rate} onChange={(e) => setF("secondary_rate", e.target.value)} disabled={!form.secondary_unit} /></Field>
            </div>
            <div className="mt-2"><span className="inline-flex rounded-lg bg-orange-50 px-4 py-2 text-sm font-bold text-orange-700">Auto Discount: {discount}% OFF</span></div>

            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <Uploader label="Catalogue Image" accept="image/*" value={form.catalogue_image} onChange={(v) => setF("catalogue_image", v)} />
              <Uploader label="Original Stock Image" accept="image/*" value={form.stock_image} onChange={(v) => setF("stock_image", v)} />
              <Uploader label="Real Stock Video" accept="video/*" value={form.stock_video} onChange={(v) => setF("stock_video", v)} />
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label="City" required><Select value={form.city} onChange={(e) => setF("city", e.target.value)}><option value="">Select</option>{filters.cities.map((c) => <option key={c.id}>{c.name}</option>)}</Select></Field>
              <Field label="Locality" required><Select value={form.locality} onChange={(e) => setF("locality", e.target.value)} disabled={!form.city}><option value="">Select</option>{locs.map((l) => <option key={l.id}>{l.name}</option>)}</Select></Field>
              <Field label="Approval"><Select value={form.approval} onChange={(e) => setF("approval", e.target.value)} data-testid="ape-approval"><option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option></Select></Field>
              <Field label="Listing Status"><Select value={form.status} onChange={(e) => setF("status", e.target.value)} data-testid="ape-status"><option value="active">Active (live)</option><option value="inactive">Inactive (hidden)</option></Select></Field>
              <label className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3"><span className="font-semibold text-slate-800">Manufacturer Stock</span><input type="checkbox" checked={form.manufacturer_stock} onChange={(e) => setF("manufacturer_stock", e.target.checked)} className="h-5 w-5" /></label>
              {form.manufacturer_stock && (
                <label className="flex items-center justify-between rounded-lg border border-orange-200 bg-orange-50/50 px-4 py-3 sm:col-span-2"><div><p className="font-semibold text-slate-800">Allow End-User (Buyer) Visibility</p><p className="text-xs text-slate-500">Also show this manufacturer stock to buyers in the public marketplace</p></div><input data-testid="ape-allowbuyers" type="checkbox" checked={form.allow_buyers} onChange={(e) => setF("allow_buyers", e.target.checked)} className="h-5 w-5" /></label>
              )}
              <label className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3"><span className="font-semibold text-slate-800">Sell Pan India</span><input type="checkbox" checked={form.pan_india} onChange={(e) => setF("pan_india", e.target.checked)} className="h-5 w-5" /></label>
            </div>

            <div className="mt-6 flex gap-2">
              <Button className="flex-1 py-3" onClick={save} disabled={busy} data-testid="admin-product-save">{busy ? "Saving…" : "Save Changes"}</Button>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
